import { toast } from 'sonner'
import { analyticsApi, getErrorMessage } from '@/lib/api'

/** Fallback when the response carries no usable Content-Disposition. */
export const DEFAULT_EXPORT_FILENAME = 'analytics-overview.xlsx'

/**
 * Pull the filename out of a Content-Disposition header.
 *
 * Handles the quoted `filename="x.xlsx"` form the backend sends and the
 * RFC 5987 `filename*=UTF-8''x.xlsx` form a proxy may rewrite it into.
 * Returns the fallback for a missing, empty or unparseable header — a
 * download must never fail just because the name couldn't be read.
 */
export function filenameFromDisposition(header?: string | null): string {
  if (!header) return DEFAULT_EXPORT_FILENAME
  const extended = header.match(/filename\*=UTF-8''([^;]+)/i)
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim())
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i)
  const name = plain?.[1]?.trim()
  return name || DEFAULT_EXPORT_FILENAME
}

/**
 * Download the Analytics overview as a spreadsheet.
 *
 * The endpoint streams bytes behind the JWT Authorization header, so a plain
 * `<a href>` can't reach it — the bytes come through the authenticated axios
 * client as a Blob, get wrapped in an object URL, and a synthetic `<a download>`
 * click saves them. Same shape as pages/materials/downloadMaterial.ts.
 *
 * The caller passes whatever filters the page is showing so the file matches
 * the screen; the backend re-derives the row scope from the caller's role, so
 * a `user_id` a Rep isn't allowed to request is refused there, not here.
 */
export async function exportAnalyticsOverview(
  params?: { period?: string; start?: string; end?: string; user_id?: number },
): Promise<void> {
  let blobUrl: string | null = null
  try {
    const res = await analyticsApi.exportReport(params)
    blobUrl = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = filenameFromDisposition(
      (res.headers as Record<string, string> | undefined)?.['content-disposition'],
    )
    document.body.appendChild(a)
    a.click()
    a.remove()
  } catch (error) {
    toast.error(getErrorMessage(error, 'Export failed. Please try again.'))
  } finally {
    if (blobUrl) {
      const url = blobUrl
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    }
  }
}
